import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { atomicHub } from 'services/api';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import powerbotByKoryLogo from 'assets/logos/powerbotbykory.svg';

type Layout = 'three-col' | 'two-col' | 'one-col';

function getLayout(width: number): Layout {
	if (width >= 980) return 'three-col';
	return 'one-col';
}

const renderBold = (chunks: React.ReactNode) => (
	<span style={{ fontWeight: 700, color: '#131629' }}>{chunks}</span>
);

const renderBoldWhite = (chunks: React.ReactNode) => (
	<span style={{ fontWeight: 700 }}>{chunks}</span>
);

const CrossSellPermissions: React.FC = () => {
	const intl = useIntl();
	const { getCurrentUserContext } = useContext(Bitrix24Context);
	const containerRef = useRef<HTMLButtonElement>(null);
	const [layout, setLayout] = useState<Layout>('three-col');

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(([entry]) => {
			setLayout(getLayout(entry.contentRect.width));
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const handleClick = async () => {
		const bx24 = (globalThis as Record<string, any>)['BX24'];
		const auth = bx24 ? bx24.getAuth() : { domain: 'solinka.bitrix24.com' };
		const domain = auth.domain;

		try {
			const user = await getCurrentUserContext(domain, auth.member_id);
			await atomicHub.post(
				'/cta-tracking/events',
				{
					eventType: 'click',
					componentType: 'banner',
					eventLocation: 'powerzap_permissions',
					applicationSource: 'powerzap',
					userContext: user,
					abTestVariant: { variant: 'yellow' },
					destinationUrl: `https://${domain}/market/detail/brs4.powerbot/?from=search&text=powerbot`,
					buttonText: intl.formatMessage({ id: 'cta_powerbot_permissions_subtitle' }),
				},
				{
					headers: {
						Authorization: `Bearer ${process.env.REACT_APP_ATOMIC_HUB_API_TOKEN}`,
					},
				},
			);
		} catch (error) {
			console.error('handleClick error', error);
		} finally {
			globalThis.open(
				`https://${domain}/market/detail/brs4.powerbot/?from=search&text=powerbot`,
				'_blank',
				'noopener,noreferrer',
			);
		}
	};

	const titleEl = (
		<div
			style={{
				fontSize: 'clamp(16px, 2vw, 22px)',
				lineHeight: 1.3,
				color: '#131629',
				fontWeight: 500,
				whiteSpace: 'pre-line',
			}}
		>
			<FormattedMessage
				id={
					layout === 'three-col'
						? 'cta_powerbot_permissions_title_large'
						: 'cta_powerbot_permissions_title'
				}
				values={{ b: renderBold }}
			/>
		</div>
	);

	const subtitleEl = (
		<div
			style={{
				fontSize: 'clamp(16px, 2vw, 22px)',
				lineHeight: 1.3,
				color: '#fffbfa',
				fontWeight: 500,
				whiteSpace: 'pre-line',
			}}
		>
			<FormattedMessage
				id={
					layout === 'three-col'
						? 'cta_powerbot_permissions_subtitle_large'
						: 'cta_powerbot_permissions_subtitle'
				}
				values={{ b: renderBoldWhite }}
			/>
		</div>
	);

	const logoEl = (
		<img
			src={powerbotByKoryLogo}
			alt="Powerbot by Kory"
			style={{
				height: 'clamp(60px, 8vw, 90px)',
				width: 'auto',
				objectFit: 'contain',
				flexShrink: 0,
			}}
		/>
	);

	const buttonStyle: React.CSSProperties = {
		width: '100%',
		display: 'flex',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
		backgroundColor: '#F5AC0D',
		borderRadius: 8,
		overflow: 'hidden',
		textAlign: 'left',
		marginTop: 2,
	};

	const whitePanelStyle: React.CSSProperties = {
		backgroundColor: '#ffffff',
		boxShadow: '4px 0 10px rgba(0,0,0,0.12)',
		flex: '0 0 auto',
		width: '30%',
		minWidth: 300,
		maxWidth: 400,
		padding: 'clamp(16px, 3vw, 28px) clamp(20px, 3vw, 36px)',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		borderTopLeftRadius: 8,
		borderBottomLeftRadius: 8,
	};

	if (layout === 'three-col') {
		return (
			<button ref={containerRef} onClick={handleClick} className="font-onest" style={buttonStyle}>
				<div style={whitePanelStyle}>{titleEl}</div>
				<div
					style={{
						flex: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 'clamp(16px, 4vw, 28px) clamp(20px, 5vw, 60px)',
					}}
				>
					{subtitleEl}
				</div>
				<div
					style={{
						flex: '0 0 auto',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 'clamp(16px, 4vw, 28px) clamp(20px, 5vw, 60px)',
					}}
				>
					{logoEl}
				</div>
			</button>
		);
	}

	return (
		<button ref={containerRef} onClick={handleClick} className="font-onest" style={buttonStyle}>
			<div
				style={{
					flex: 1,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					padding: 'clamp(16px, 4vw, 28px) clamp(16px, 5vw, 60px)',
					paddingRight: 'clamp(32px, 6vw, 80px)',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
					<div
						style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}
					>
						{titleEl}
						{subtitleEl}
					</div>
					{logoEl}
				</div>
			</div>
		</button>
	);
};

export default CrossSellPermissions;
